<script setup>
import { nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { Switch, SwitchGroup, SwitchLabel } from '@headlessui/vue';
import { cookieConsentSubmission } from '../../helpers/cookieConsentRecorder';
import { CATEGORIES, currentCategories, decisionCategories, planChoice } from '../../utils/cookieConsentChoice.mjs';
import {
    FUNCTIONAL_ACCEPTED_EVENT,
    acceptedTrackers,
    functionalCookiesAccepted,
} from '../../helpers/cookieBannerConsent';
import { COOKIE_SETTINGS_HASH, cookieBannerMounted } from '../../helpers/cookieSettings';

const props = defineProps({
    initiallyVisible: {
        type: Boolean,
        default: true,
    },
    privacyPolicyUrl: {
        type: String,
        required: true,
    },
    cookiePolicyUrl: {
        type: String,
        default: null,
    },
    categoryTrackers: {
        type: Object,
        required: true,
    },
    sessionCookie: {
        type: String,
        required: true,
    },
    sessionLifetimeMinutes: {
        type: Number,
        required: true,
    },
    cookieLifetimeDays: {
        type: Number,
        required: true,
    },
    bannerVersion: {
        type: String,
        required: true,
    },
});

// The optional categories in the settings view, each with the providers that
// set its cookies and where to read about them.
const CATEGORY_ROWS = [
    {
        id: 'analytics',
        labelKey: 'cookieBanner:category.analytics.label',
        descriptionKey: 'cookieBanner:category.analytics.description',
        providers: [
            { name: 'Google Analytics', url: 'https://policies.google.com/technologies/cookies' },
        ],
    },
    {
        id: 'marketing',
        labelKey: 'cookieBanner:category.marketing.label',
        descriptionKey: 'cookieBanner:category.marketing.description',
        providers: [
            { name: 'Meta', url: 'https://www.facebook.com/privacy/policies/cookies/' },
            { name: 'LinkedIn', url: 'https://www.linkedin.com/legal/cookie-policy' },
        ],
    },
    {
        id: 'embedded',
        labelKey: 'cookieBanner:category.embedded.label',
        descriptionKey: 'cookieBanner:category.embedded.description',
        providers: [
            { name: 'Calendly', url: 'https://calendly.com/legal/privacy-notice' },
            { name: 'Vimeo', url: 'https://vimeo.com/legal/privacy/cookies' },
            { name: 'Whereby', url: 'https://whereby.com/information/tos/cookie-policy/' },
        ],
    },
];

// Our own cookies, which need no consent.
const NECESSARY_COOKIES = [
    { name: props.sessionCookie, purposeKey: 'cookieBanner:cookiePurpose.session', durationKey: 'cookieBanner:duration.minutes', count: props.sessionLifetimeMinutes },
    { name: 'XSRF-TOKEN', purposeKey: 'cookieBanner:cookiePurpose.xsrf', durationKey: 'cookieBanner:duration.minutes', count: props.sessionLifetimeMinutes },
    { name: 'consentId', purposeKey: 'cookieBanner:cookiePurpose.consentId', durationKey: 'cookieBanner:duration.days', count: props.cookieLifetimeDays },
    { name: 'acceptedTrackers', purposeKey: 'cookieBanner:cookiePurpose.choice', durationKey: 'cookieBanner:duration.days', count: props.cookieLifetimeDays },
    { name: 'acceptedFunctional', purposeKey: 'cookieBanner:cookiePurpose.choice', durationKey: 'cookieBanner:duration.days', count: props.cookieLifetimeDays },
    { name: 'consentVersion', purposeKey: 'cookieBanner:cookiePurpose.choice', durationKey: 'cookieBanner:duration.days', count: props.cookieLifetimeDays },
];

const visible = ref(props.initiallyVisible);
const view = ref('main');
const settingsHeading = ref(null);
const selection = reactive(Object.fromEntries(CATEGORIES.map((category) => [category, false])));
const saving = ref(null);
const saveFailed = ref(false);

const submission = cookieConsentSubmission();

const storedCategories = () => currentCategories(
    { trackers: acceptedTrackers(), functional: functionalCookiesAccepted() },
    props.categoryTrackers,
);

const writeChoiceCookies = ({ trackers, functional }) => {
    const expires = new Date(Date.now() + props.cookieLifetimeDays * 24 * 60 * 60 * 1000).toUTCString();

    document.cookie = `acceptedTrackers=${trackers.join(',')};expires=${expires};path=/`;
    document.cookie = `acceptedFunctional=${functional ? 1 : 0};expires=${expires};path=/`;
    // A choice made on an older banner version brings the banner back for the visitor.
    document.cookie = `consentVersion=${props.bannerVersion};expires=${expires};path=/`;
};

const showSettings = async () => {
    // Every category starts off unless the visitor already allowed it.
    const allowed = storedCategories();
    CATEGORIES.forEach((category) => {
        selection[category] = allowed.includes(category);
    });

    saveFailed.value = false;
    view.value = 'settings';
    await nextTick();
    settingsHeading.value?.focus();
};

const close = () => {
    visible.value = false;
    view.value = 'main';
    saveFailed.value = false;

    if (window.location.hash === COOKIE_SETTINGS_HASH) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
    }
};

const choose = async (decision) => {
    const categories = decisionCategories(decision, CATEGORIES.filter((category) => selection[category]));
    const plan = planChoice(storedCategories(), categories, props.categoryTrackers);
    const choice = { decision, categories };

    if (plan.recordFirst) {
        saving.value = decision;
        saveFailed.value = false;
        const recorded = await submission.recordFirst(choice);
        saving.value = null;

        if (!recorded) {
            saveFailed.value = true;
            return;
        }
    }

    writeChoiceCookies(plan.cookies);
    close();

    if (plan.reload) {
        // Taking anything away: only a reload removes what is already loaded.
        // An unrecorded choice is retried after the reload.
        if (!plan.recordFirst) {
            await Promise.race([submission.recordLater(choice), new Promise((resolve) => setTimeout(resolve, 2000))]);
        }
        window.location.reload();
        return;
    }

    if (plan.startEmbedded) {
        window.dispatchEvent(new CustomEvent(FUNCTIONAL_ACCEPTED_EVENT));
    }

    if (plan.startTrackers.length > 0) {
        window.dispatchEvent(new CustomEvent('cookieAccepted', { detail: { trackers: plan.startTrackers } }));
    }

    if (!plan.recordFirst) {
        submission.recordLater(choice);
    }
};

// The "Cookie settings" links open straight into the settings view.
const open = () => {
    visible.value = true;
    showSettings();
};

const onDocumentClick = (event) => {
    const link = event.target.closest?.(`a[href="${COOKIE_SETTINGS_HASH}"]`);

    if (link) {
        event.preventDefault();
        open();
    }
};

onMounted(() => {
    cookieBannerMounted.value = true;
    document.addEventListener('click', onDocumentClick);

    if (submission.hasPending()) {
        submission.flushPending();
    }

    if (window.location.hash === COOKIE_SETTINGS_HASH) {
        open();
    }
});

onBeforeUnmount(() => {
    cookieBannerMounted.value = false;
    document.removeEventListener('click', onDocumentClick);
});
</script>

<template>
    <div
        v-if="visible"
        id="cookie-banner"
        class="fixed bottom-8 left-1/2 transform -translate-x-1/2 p-4 lg:p-8 text-center max-w-3xl w-full max-h-[calc(100vh-4rem)] overflow-y-auto shadow-md z-50 rounded-wl bg-white"
    >
        <template v-if="view === 'main'">
            <div class="mb-4 space-y-2 text-xs text-left">
                <p class="!mb-0">
                    <mf-translate domainkey="cookieBanner:intro"></mf-translate>
                </p>
                <ul class="pl-5 list-disc !mt-0">
                    <li>
                        <span class="font-semibold"><mf-translate domainkey="cookieBanner:category.embedded.label"></mf-translate>: </span>
                        <mf-translate domainkey="cookieBanner:category.embedded.description"></mf-translate>
                    </li>
                    <li>
                        <span class="font-semibold"><mf-translate domainkey="cookieBanner:category.analytics.label"></mf-translate>: </span>
                        <mf-translate domainkey="cookieBanner:category.analytics.description"></mf-translate>
                    </li>
                    <li>
                        <span class="font-semibold"><mf-translate domainkey="cookieBanner:category.marketing.label"></mf-translate>: </span>
                        <mf-translate domainkey="cookieBanner:category.marketing.description"></mf-translate>
                    </li>
                </ul>
                <p class="!mt-0">
                    <mf-translate domainkey="cookieBanner:outro"></mf-translate>
                    <template v-if="cookiePolicyUrl">
                        <a class="ml-1 mf-link" :href="cookiePolicyUrl">
                            <mf-translate domainkey="cookieBanner:cookiePolicyLabel"></mf-translate>
                        </a>
                        ·
                    </template>
                    <a class="mf-link" :class="{ 'ml-1': !cookiePolicyUrl }" :href="privacyPolicyUrl">
                        <mf-translate domainkey="auth:terms.integrityPolicy"></mf-translate>
                    </a>
                </p>
            </div>

            <div class="flex flex-wrap justify-center gap-2">
                <button-secondary
                    size="sm"
                    id="cookie-banner-decline-cookies"
                    :disabled="saving !== null"
                    :state="saving === 'declined' ? 'waiting' : ''"
                    @click="choose('declined')"
                >
                    <mf-translate domainkey="cookieBanner:declineCookies"></mf-translate>
                </button-secondary>

                <button-secondary
                    size="sm"
                    id="cookie-banner-choose-settings"
                    :disabled="saving !== null"
                    @click="showSettings"
                >
                    <mf-translate domainkey="cookieBanner:chooseSettings"></mf-translate>
                </button-secondary>

                <button-primary
                    size="sm"
                    id="cookie-banner-accept-cookies"
                    :disabled="saving !== null"
                    :state="saving === 'accepted' ? 'waiting' : ''"
                    @click="choose('accepted')"
                >
                    <mf-translate domainkey="cookieBanner:acceptCookies"></mf-translate>
                </button-primary>
            </div>
        </template>

        <template v-else>
            <h2 ref="settingsHeading" tabindex="-1" class="mb-2 text-sm font-semibold text-left focus:outline-none">
                <mf-translate domainkey="cookieBanner:cookieSettings"></mf-translate>
            </h2>

            <ul class="mb-4 text-xs text-left divide-y divide-gray-200">
                <li class="py-3">
                    <SwitchGroup as="div" class="flex items-center justify-between gap-4">
                        <SwitchLabel as="span" class="font-semibold" passive>
                            <mf-translate domainkey="cookieBanner:category.necessary.label"></mf-translate>
                        </SwitchLabel>
                        <span class="flex items-center gap-2 text-gray-500">
                            <mf-translate domainkey="cookieBanner:alwaysOn"></mf-translate>
                            <Switch :model-value="true" disabled class="relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-400 bg-gray-400 cursor-not-allowed">
                                <span aria-hidden="true" class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition duration-200 translate-x-4"></span>
                            </Switch>
                        </span>
                    </SwitchGroup>
                    <p class="mt-1 !mb-0">
                        <mf-translate domainkey="cookieBanner:category.necessary.description"></mf-translate>
                    </p>
                    <p class="mt-1 !mb-0 text-gray-600">
                        <mf-translate domainkey="cookieBanner:providersLabel"></mf-translate>:
                        <mf-translate domainkey="cookieBanner:provider.thisWebsite"></mf-translate>
                    </p>
                    <details class="mt-1 !mb-0">
                        <summary class="cursor-pointer mf-link w-fit">
                            <mf-translate domainkey="cookieBanner:cookieDetails"></mf-translate>
                        </summary>
                        <table class="w-full">
                            <thead>
                                <tr class="text-gray-600">
                                    <th class="pr-3 font-medium text-left"><mf-translate domainkey="cookieBanner:cookieTable.cookie"></mf-translate></th>
                                    <th class="pr-3 font-medium text-left"><mf-translate domainkey="cookieBanner:cookieTable.purpose"></mf-translate></th>
                                    <th class="font-medium text-left"><mf-translate domainkey="cookieBanner:cookieTable.duration"></mf-translate></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="cookie in NECESSARY_COOKIES" :key="cookie.name" class="align-top">
                                    <td class="pr-3 font-mono break-all">{{ cookie.name }}</td>
                                    <td class="pr-3"><mf-translate :domainkey="cookie.purposeKey"></mf-translate></td>
                                    <td class="whitespace-nowrap"><mf-translate :domainkey="cookie.durationKey" :args="{ count: cookie.count }"></mf-translate></td>
                                </tr>
                            </tbody>
                        </table>
                    </details>
                </li>

                <li v-for="row in CATEGORY_ROWS" :key="row.id" class="py-3">
                    <SwitchGroup as="div" class="flex items-center justify-between gap-4">
                        <SwitchLabel as="span" class="font-semibold cursor-pointer">
                            <mf-translate :domainkey="row.labelKey"></mf-translate>
                        </SwitchLabel>
                        <Switch
                            v-model="selection[row.id]"
                            :disabled="saving !== null"
                            :class="[selection[row.id] ? 'bg-green-600' : 'bg-gray-300', 'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-400 cursor-pointer']"
                        >
                            <span
                                aria-hidden="true"
                                :class="[selection[row.id] ? 'translate-x-4' : 'translate-x-0', 'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition duration-200']"
                            ></span>
                        </Switch>
                    </SwitchGroup>
                    <!-- The descriptions are shared with the main view, where they follow "Category:". -->
                    <p class="!mt-0 !mb-0 first-letter:uppercase">
                        <mf-translate :domainkey="row.descriptionKey"></mf-translate>
                    </p>
                    <p class="!mt-0 !mb-0 text-gray-600">
                        <mf-translate domainkey="cookieBanner:providersLabel"></mf-translate>:
                        <template v-for="(provider, index) in row.providers" :key="provider.name">
                            <template v-if="index > 0">, </template>
                            {{ provider.name }}
                            (<a class="mf-link" :href="provider.url" target="_blank" rel="noopener noreferrer"><mf-translate domainkey="cookieBanner:cookieDetails"></mf-translate></a>)
                        </template>
                    </p>
                </li>
            </ul>

            <div class="flex flex-wrap justify-center gap-2">
                <button-secondary
                    size="sm"
                    id="cookie-banner-settings-decline-cookies"
                    :disabled="saving !== null"
                    :state="saving === 'declined' ? 'waiting' : ''"
                    @click="choose('declined')"
                >
                    <mf-translate domainkey="cookieBanner:declineCookies"></mf-translate>
                </button-secondary>

                <button-secondary
                    size="sm"
                    id="cookie-banner-save-choices"
                    :disabled="saving !== null"
                    :state="saving === 'custom' ? 'waiting' : ''"
                    @click="choose('custom')"
                >
                    <mf-translate domainkey="cookieBanner:saveChoices"></mf-translate>
                </button-secondary>

                <button-primary
                    size="sm"
                    id="cookie-banner-settings-accept-cookies"
                    :disabled="saving !== null"
                    :state="saving === 'accepted' ? 'waiting' : ''"
                    @click="choose('accepted')"
                >
                    <mf-translate domainkey="cookieBanner:acceptCookies"></mf-translate>
                </button-primary>
            </div>
        </template>

        <p v-if="saveFailed" class="mt-2 text-xs text-myflowRed" role="alert">
            <mf-translate domainkey="cookieBanner:saveFailed"></mf-translate>
        </p>
    </div>
</template>
